import React, { useMemo, useRef, useState } from "react";

import {
  faEye,
  faLink,
  faPage,
  faPlay,
  faPlus,
  faWarning,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { isAfter } from "date-fns";
import { twJoin } from "tailwind-merge";
import { useOnClickOutside } from "usehooks-ts";

import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import {
  TableComp as Table,
  Cell as TableCell,
  Header as TableHeader,
} from "src/base-components/Table";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon, IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { buildTestRunParamValue } from "src/flowContainer/TestRunsSidePane/hooks";
import { JobRunStatus } from "src/flowContainer/TestRunsSidePane/JobRunStatus";
import { JobRun, JobRunStatusType } from "src/jobs/types";
import { useFlowContext } from "src/router/routerContextHooks";
import { LeftPaneOptions } from "src/router/SearchParams";
import { getBaseUrl, getUrlToAuthoringPage } from "src/router/urls";
import { formatDate } from "src/utils/datetime";

const columnTextClassName = "text-gray-800 font-inter-medium-13px";
const datasetColumnSize = 220;
const helper = createColumnHelper<JobRun>();

const canLoadTestRun = (jobRun: JobRun) => {
  return [
    JobRunStatusType.Completed,
    JobRunStatusType.Running,
    JobRunStatusType.Writing,
  ].includes(jobRun.status.type);
};

const renderActions = ({
  canLoad,
  isOutdated,
  isRunning,
  stopTestRun,
  loadTestRun,
}: {
  isOutdated: boolean;
  isRunning: boolean;
  canLoad: boolean;
  stopTestRun: () => void;
  loadTestRun: () => void;
}) => {
  const actions: React.ReactNode[] = [
    canLoad && (
      <Tooltip key="see-results" title="See results" placement="top" asChild>
        <IconButton
          icon={faEye}
          onClick={(e) => {
            e.stopPropagation();
            loadTestRun();
          }}
          size="xs"
        />
      </Tooltip>
    ),

    isOutdated && (
      <Tooltip
        key="incompatible-test-run"
        title="Incompatible test run"
        placement="right"
        body="This decision flow was changed after this test run. You might see incomplete results."
        asChild
      >
        <Icon icon={faWarning} size="xs" variant="warning" />
      </Tooltip>
    ),

    isRunning && (
      <IconButton
        key="stop-test-run"
        icon={faXmark}
        onClick={(e) => {
          e.stopPropagation();
          stopTestRun();
        }}
        size="xs"
      />
    ),
  ];

  const filteredActions = actions.filter(Boolean);

  if (filteredActions.length === 1) {
    filteredActions.push(<div key="spacer" className="w-7" />);
  }

  return filteredActions;
};

const getColumns = ({
  orgId,
  workspaceId,
  flowId,
}: {
  orgId: string;
  workspaceId: string;
  flowId: string;
}) => [
  helper.display({
    id: "dataset",
    header: (info) => (
      <TableHeader dataLoc="test-run-dataset-header" info={info}>
        <span className={twJoin("px-4", columnTextClassName)}>Test data</span>
      </TableHeader>
    ),
    cell: (info) => {
      const actions = info.table.options.meta?.actions;
      const isLoaded = Boolean(actions?.isLoaded(info.row.original.id));
      return (
        <TableCell
          className={twJoin("px-3.5", isLoaded && "bg-gray-50")}
          info={info}
        >
          <div
            className="flex h-full w-full items-center"
            data-loc={info.row.original.id}
          >
            <div className="flex items-center overflow-hidden">
              <Icon icon={faPage} size="xs" />
              <div className="min-w-0 flex-1 truncate font-inter-medium-12px text-gray-700">
                {actions?.getDatasetNameBySourceId(
                  info.row.original.source_id,
                ) || "Unknown Dataset"}
              </div>
            </div>
          </div>
        </TableCell>
      );
    },
    size: datasetColumnSize,
    minSize: datasetColumnSize,
    maxSize: datasetColumnSize,
  }),
  helper.display({
    id: "results",
    header: (info) => (
      <TableHeader dataLoc="results-header" info={info}>
        <span className={columnTextClassName}>Results</span>
      </TableHeader>
    ),
    cell: (info) => {
      const actions = info.table.options.meta?.actions;
      const isLoaded = Boolean(actions?.isLoaded(info.row.original.id));
      return (
        <TableCell className={twJoin(isLoaded && "bg-gray-50")} info={info}>
          <div className="flex h-full w-full items-center justify-between gap-x-2">
            <div className="flex-1">
              <JobRunStatus
                jobRun={info.row.original}
                footer={getFooter(
                  actions?.getUsernameById(
                    "triggered_by" in info.row.original.trigger
                      ? info.row.original.trigger.triggered_by
                      : null,
                  ) ?? null,
                  info.row.original.created_at,
                )}
                showDetails={
                  actions?.shouldShowDetails(info.row.original.id) ?? false
                }
                onToggleDetails={() => {
                  actions?.toggleShowDetails(info.row.original.id);
                }}
              />
            </div>
            <div
              className={twJoin(
                "ml-auto flex w-21 items-center gap-x-2 group-hover:opacity-100",
                isLoaded ? "opacity-100" : "opacity-0",
              )}
              data-loc="test-run-row-buttons-container"
            >
              <CopyTextIcon
                tooltip="Copy test run url"
                tooltipPlacement="top"
                icon={faLink}
                value={
                  getBaseUrl() +
                  getUrlToAuthoringPage(
                    orgId,
                    workspaceId,
                    flowId,
                    info.row.original.flow_version_id ?? "",
                    {
                      testRunId: buildTestRunParamValue(info.row.original),
                      leftPane: LeftPaneOptions.TestRuns,
                    },
                  )
                }
              />
              {renderActions({
                isOutdated: Boolean(
                  actions?.isOutdatedTestRun(info.row.original),
                ),
                isRunning: isTestRunRunning(info.row.original),
                canLoad: canLoadTestRun(info.row.original),
                stopTestRun: () => {
                  actions?.stopTestRun(info.row.original);
                },
                loadTestRun: () => {
                  actions?.loadTestRun(info.row.original);
                },
              })}
            </div>
          </div>
        </TableCell>
      );
    },
  }),
];

export const TestRunsTable: React.FC<{
  lastUpdatedAt: string;
  loadedTestRunId: string | null;
  jobRuns: JobRun[];
  datasetNamesBySourceId: Record<string, string>;
  usernameById: Record<string, string | null>;
  isLoading: boolean;
  isEmpty: boolean;
  onStop: (jobRun: JobRun) => void;
  onLoad: (jobRun: JobRun) => void;
}> = ({
  datasetNamesBySourceId,
  loadedTestRunId,
  usernameById,
  isLoading,
  isEmpty,
  jobRuns,
  lastUpdatedAt,
  onStop,
  onLoad,
}) => {
  const { orgId, workspace, flow } = useFlowContext();
  const [showDetailsFor, setShowDetailsFor] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const columns = useMemo(
    () => getColumns({ orgId, workspaceId: workspace.id, flowId: flow.id }),
    [workspace.id, flow.id, orgId],
  );

  useOnClickOutside(containerRef, (e) => {
    // Do not close the details popover if the click is inside the popover
    if (
      e.target instanceof HTMLElement &&
      e.target.closest("[data-floating-ui-focusable]")
    ) {
      return;
    }

    setShowDetailsFor(null);
  });

  const handleToggleShowDetails = (testRunId: string) => {
    if (showDetailsFor === testRunId) {
      setShowDetailsFor(null);
    } else {
      setShowDetailsFor(testRunId);
    }
  };

  if (isLoading || !isEmpty) {
    return (
      <div ref={containerRef} className="bg-white">
        <Table
          columns={columns}
          data={jobRuns}
          dataLoc="test-runs-table"
          frameClassName="h-auto border-gray-100 border-b"
          isLoading={isLoading}
          noScroll={true}
          rowClassName="group hover:bg-gray-50"
          headerPropsGetter={() => ({
            onClick: () => setShowDetailsFor(null),
          })}
          rowPropsGetter={(row) => ({
            onClick: () => handleToggleShowDetails(row.original.id),
            classNameOverrides: twJoin(
              canLoadTestRun(row.original) && "cursor-pointer",
            ),
          })}
          actions={{
            getDatasetNameBySourceId: (sourceId: string) => {
              return datasetNamesBySourceId[sourceId];
            },
            getUsernameById: (userId: string) => {
              return usernameById[userId];
            },
            isOutdatedTestRun: (testRun: JobRun) => {
              return isAfter(lastUpdatedAt, testRun.created_at);
            },
            stopTestRun: onStop,
            loadTestRun: onLoad,
            isLoaded: (testRunId: string) => {
              return testRunId === loadedTestRunId;
            },
            shouldShowDetails: (testRunId: string) => {
              return showDetailsFor === testRunId;
            },
            toggleShowDetails: handleToggleShowDetails,
          }}
        />
      </div>
    );
  }

  return (
    <EmptyState
      actionButton={{
        label: "New test run",
        icon: faPlus,
        dataLoc: "test-runs-create-test-run-button",
        onClick: () => {},
      }}
      description="Create your first test run"
      headline="No test runs available"
      icon={faPlay}
    />
  );
};

const isTestRunRunning = (jobRun: JobRun) => {
  return jobRun.status.type === JobRunStatusType.Running;
};

const getFooter = (username: string | null, createdAt: string) => {
  if (username) {
    return `Test started by ${username} on ${formatDate(createdAt)}`;
  }

  return `Test started on ${formatDate(createdAt)}`;
};
